import { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { clsx } from 'clsx'
import { useLayoutVariant } from '../../hooks/useLayoutVariant'

export default function Select({ 
  options = [], 
  value, 
  onChange, 
  label, 
  error, 
  placeholder = 'Seleccionar...',
  disabled = false,
  className = ''
}) {
  const { isAdminAppUI } = useLayoutVariant()
  const selected = options.find(opt => opt.value === value)

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label className={clsx(
          'block text-sm font-medium mb-1.5',
          isAdminAppUI ? 'text-[#7a8fa6]' : 'text-pastel-ink dark:text-gray-300'
        )}>
          {label}
          <span className="text-red-500 ml-1">{/* required indicator */}</span>
        </label>
      )}
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <Listbox.Button className={clsx(
            'relative w-full cursor-default border py-2.5 pl-3 pr-10 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0',
            isAdminAppUI ? 'rounded-[10px]' : 'rounded-2xl',
            error
              ? (isAdminAppUI
                ? 'border-[#ef5350] text-[#ef5350] bg-[#1f2d40] focus:ring-[#ef5350]/25'
                : 'border-red-300 dark:border-red-600 text-red-900 dark:text-red-200 bg-white dark:bg-gray-800 focus:ring-red-500 dark:focus:ring-red-500')
              : (isAdminAppUI
                ? 'border-[#243246] text-[#e8edf5] bg-[#1f2d40] focus:ring-[#00c896]/25 focus:border-[#00c896]'
                : 'border-pastel-pink dark:border-gray-600 text-pastel-ink dark:text-gray-100 bg-white dark:bg-gray-800 focus:ring-pastel-teal/40 dark:focus:ring-primary-400'),
            disabled && (isAdminAppUI ? 'opacity-50 cursor-not-allowed bg-[#1a2535]' : 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900')
          )}>
            <span className={clsx(
              'block truncate',
              !selected && (isAdminAppUI ? 'text-[#3d5166]' : 'text-gray-500 dark:text-gray-400')
            )}>
              {selected ? selected.label : placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className={clsx(
                  'h-5 w-5',
                  isAdminAppUI ? 'text-[#3d5166]' : 'text-gray-400 dark:text-gray-500'
                )}
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className={clsx(
              'absolute z-50 mt-1 max-h-60 w-full overflow-auto py-1 text-base focus:outline-none',
              isAdminAppUI
                ? 'rounded-[10px] bg-[#1a2535] ring-1 ring-[#243246] shadow-xl'
                : 'rounded-2xl bg-white dark:bg-gray-800 shadow-medium ring-1 ring-pastel-pink dark:ring-gray-700'
            )}>
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  className={({ active }) =>
                    clsx(
                      'relative cursor-default select-none py-2 pl-10 pr-4',
                      isAdminAppUI
                        ? active
                          ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]'
                          : 'text-[#e8edf5]'
                        : active
                          ? 'bg-pastel-cream text-pastel-ink'
                          : 'text-pastel-ink dark:text-gray-100'
                    )
                  }
                  value={option.value}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={clsx(
                          'block truncate',
                          selected ? 'font-medium' : 'font-normal'
                        )}
                      >
                        {option.label}
                      </span>
                      {selected ? (
                        <span className={clsx(
                          'absolute inset-y-0 left-0 flex items-center pl-3',
                          isAdminAppUI ? 'text-[#00c896]' : 'text-pastel-teal dark:text-primary-400'
                        )}>
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {error && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  )
}

